import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DunningMessageOverrides } from "@/lib/dunningBanner";

export const DUNNING_MESSAGES_QUERY_KEY = ["banner_config", "messages"] as const;

/**
 * Admin-edited wording for the subscription dunning banner, keyed by
 * `banner_config.banner_key`. Only the message is read: the banner's button,
 * destination and colours are decided in code (store anti-steering rules and
 * contrast), so the other columns of that table are not consulted.
 *
 * `enabled` should be true only for a member who is actually shown a banner,
 * so the other ~all members never make this request.
 *
 * Returns undefined while loading and on error. The banner then shows its
 * built-in wording, which is the fail-open answer: a member in dunning must
 * see the nudge even when this read fails.
 */
export function useDunningMessages(enabled: boolean): DunningMessageOverrides | undefined {
  const { data } = useQuery<DunningMessageOverrides>({
    queryKey: DUNNING_MESSAGES_QUERY_KEY,
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banner_config")
        .select("banner_key, message");
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((row) => [row.banner_key, row.message]));
    },
  });
  return data;
}
